import React from 'react';
import './EventsList.css';

const EventsList = ({ events, viewMode, onEdit, onDelete, onManageRSVPs, onInvite, loading }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { label: '⏳ Draft', className: 'badge-warning' },
      published: { label: '✓ Published', className: 'badge-success' },
      cancelled: { label: '✗ Cancelled', className: 'badge-danger' },
      completed: { label: '✓ Completed', className: 'badge-info' }
    };
    return badges[status] || badges.draft;
  };

  const getLocationIcon = (type) => {
    return type === 'virtual' ? '💻' : '📍';
  };

  const currencySymbols = { INR: '₹', USD: '$', AUD: 'A$', GBP: '£', EUR: '€' };

  const renderPricingCell = (event) => {
    const isFree =
      event.is_free === true ||
      (!Array.isArray(event.prices) || event.prices.length === 0) &&
        (event.price === null || event.price === undefined || Number(event.price) === 0);

    const enabledPrices = Array.isArray(event.prices)
      ? event.prices.filter((p) => p.isenabled !== false)
      : [];

    if (isFree && enabledPrices.length === 0) {
      return (
        <span
          style={{
            display: 'inline-block',
            padding: '3px 10px',
            borderRadius: '999px',
            fontSize: '12px',
            fontWeight: 700,
            background: 'rgba(34,197,94,0.12)',
            color: '#16a34a',
            border: '1px solid rgba(34,197,94,0.35)',
          }}
        >
          Free
        </span>
      );
    }

    if (enabledPrices.length === 0 && event.price !== null && event.price !== undefined) {
      // Legacy single-price field fallback
      enabledPrices.push({ currency: event.currency || 'INR', price: event.price, isenabled: true });
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {enabledPrices.map((p) => {
          const sym = currencySymbols[p.currency] || p.currency;
          const amt = Number(p.price);
          return (
            <span
              key={p.currency}
              style={{
                display: 'inline-block',
                padding: '3px 10px',
                borderRadius: '999px',
                fontSize: '12px',
                fontWeight: 700,
                background: 'rgba(239,68,68,0.10)',
                color: '#dc2626',
                border: '1px solid rgba(239,68,68,0.30)',
                whiteSpace: 'nowrap',
              }}
            >
              {sym}{amt % 1 === 0 ? amt.toLocaleString() : amt.toFixed(2)}
              <span style={{ fontWeight: 400, color: '#888', marginLeft: 4 }}>{p.currency}</span>
            </span>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="events-loading">
        <p>Loading events...</p>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="placeholder-content">
        <div className="placeholder-icon">📅</div>
        <h3>No Events Found</h3>
        <p>Start by creating your first event.</p>
      </div>
    );
  }

  if (viewMode === 'grid') {
    return (
      <div className="events-grid">
        {events.map((event) => (
          <div key={event.id} className="event-grid-card">
            <div className="event-thumbnail">
              {event.image_url || event.thumbnail_url ? (
                <img src={event.thumbnail_url || event.image_url} alt={event.title} />
              ) : (
                <div className="event-thumbnail-placeholder">
                  <span>📅</span>
                </div>
              )}
              <div className="event-badges">
                {event.is_featured && (
                  <span className="badge badge-featured">⭐ Featured</span>
                )}
                <span className={`badge ${getStatusBadge(event.status).className}`}>
                  {getStatusBadge(event.status).label}
                </span>
              </div>
            </div>
            
            <div className="event-card-content">
              <div className="event-card-header">
                <span className="event-category-badge">
                  {event.category_name || 'Uncategorized'}
                </span>
                <span className="event-location-type">
                  {getLocationIcon(event.location_type)} {event.location_type}
                </span>
              </div>
              
              <h3 className="event-card-title">{event.title}</h3>
              
              {event.description && (
                <p className="event-card-description">
                  {event.description.substring(0, 100)}
                  {event.description.length > 100 ? '...' : ''}
                </p>
              )}
              
              <div className="event-card-meta">
                <div className="event-date">
                  <span>📅 {formatDate(event.start_datetime)}</span>
                </div>
                <div className="event-location">
                  <span>{event.location_name || event.city || 'TBD'}</span>
                </div>
                <div className="event-location">
                  <span>💰 </span>{renderPricingCell(event)}
                </div>
              </div>
              
              <div className="event-card-stats">
                <span title="Going">✅ {event.going_count || 0}</span>
                <span title="Interested">⭐ {event.interested_count || 0}</span>
                <span title="Comments">💬 {event.comment_count || 0}</span>
                {event.max_attendees && (
                  <span title="Capacity">👥 {event.going_count || 0}/{event.max_attendees}</span>
                )}
              </div>
              
              <div className="event-card-footer">
                <div className="event-card-organizer">
                  By: {event.organizer_name || 'Unknown'}
                </div>
                <div className="event-card-actions">
                  <button
                    className="action-btn action-btn-edit"
                    onClick={() => onEdit(event)}
                    title="Edit Event"
                  >
                    ✏️
                  </button>
                  <button
                    className="action-btn action-btn-rsvp"
                    onClick={() => onManageRSVPs(event)}
                    title="Manage RSVPs"
                  >
                    👥
                  </button>
                  <button
                    className="action-btn action-btn-email"
                    onClick={() => onInvite(event)}
                    title="Send Invites"
                  >
                    📨
                  </button>
                  <button
                    className="action-btn action-btn-delete"
                    onClick={() => onDelete(event)}
                    title="Delete Event"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // List view
  return (
    <div className="table-container">
      <table className="admin-table events-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Event</th>
            <th>Date & Time</th>
            <th>Location</th>
            <th>Category</th>
            <th>Pricing</th>
            <th>Status</th>
            <th>RSVPs</th>
            <th>Capacity</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {events.map((event) => (
            <tr key={event.id}>
              <td>{event.id}</td>
              <td>
                <div className="event-info">
                  <div className="event-thumbnail-small">
                    {event.thumbnail_url ? (
                      <img src={event.thumbnail_url} alt={event.title} />
                    ) : (
                      <span>📅</span>
                    )}
                  </div>
                  <div>
                    <strong>{event.title}</strong>
                    {event.is_featured && <span className="badge badge-featured-small">⭐</span>}
                    <div className="event-organizer-small">
                      By: {event.organizer_name || 'Unknown'}
                    </div>
                  </div>
                </div>
              </td>
              <td>
                <div className="event-datetime">
                  <div>{formatDate(event.start_datetime)}</div>
                  <small>to</small>
                  <div>{formatDate(event.end_datetime)}</div>
                </div>
              </td>
              <td>
                <div className="event-location-info">
                  <span className="location-type-badge">
                    {getLocationIcon(event.location_type)} {event.location_type}
                  </span>
                  <div className="location-name">
                    {event.location_name || event.city || 'TBD'}
                  </div>
                </div>
              </td>
              <td>
                <span className="badge badge-category">
                  {event.category_name || 'Uncategorized'}
                </span>
              </td>
              <td>
                {renderPricingCell(event)}
              </td>
              <td>
                <span className={`badge ${getStatusBadge(event.status).className}`}>
                  {getStatusBadge(event.status).label}
                </span>
                {event.is_public ? (
                  <div className="visibility-badge">🌐 Public</div>
                ) : (
                  <div className="visibility-badge">🔒 Private</div>
                )}
              </td>
              <td>
                <div className="rsvp-stats">
                  <div>✅ {event.going_count || 0} Going</div>
                  <div>⭐ {event.interested_count || 0} Interested</div>
                  <div>💬 {event.comment_count || 0} Comments</div>
                </div>
              </td>
              <td>
                {event.max_attendees ? (
                  <div className="capacity-info">
                    <div className="capacity-bar">
                      <div 
                        className="capacity-fill"
                        style={{ 
                          width: `${Math.min((event.going_count / event.max_attendees) * 100, 100)}%`,
                          backgroundColor: (event.going_count / event.max_attendees) > 0.9 ? '#ef4444' : '#22c55e'
                        }}
                      ></div>
                    </div>
                    <small>{event.going_count || 0} / {event.max_attendees}</small>
                  </div>
                ) : (
                  <span className="unlimited">Unlimited</span>
                )}
              </td>
              <td>
                <div className="action-buttons">
                  <button
                    className="action-btn action-btn-edit"
                    onClick={() => onEdit(event)}
                    title="Edit Event"
                  >
                    ✏️
                  </button>
                  <button
                    className="action-btn action-btn-rsvp"
                    onClick={() => onManageRSVPs(event)}
                    title="Manage RSVPs"
                  >
                    👥
                  </button>
                  <button
                    className="action-btn action-btn-email"
                    onClick={() => onInvite(event)}
                    title="Send Invites"
                  >
                    📨 Invite
                  </button>
                  <button
                    className="action-btn action-btn-delete"
                    onClick={() => onDelete(event)}
                    title="Delete Event"
                  >
                    🗑️
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default EventsList;
